<script setup lang="ts">
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {
    ActionComponentProps,
    EdgePath,
    FLOWCHART_INSTANCE_OPTIONS,
    FlowchartInstancePath,
    OptionField,
    ResolvedAction
} from "@/core/interfaces/flowchart/flowchart";
import {X} from "@lucide/vue";
import {computed, ref, watch} from "vue";
import ContactActionDialogContent from "@/flowcharts/Components/ContactActionDialogContent.vue";
import PrintActionDialogContent from "@/flowcharts/Components/PrintActionDialogContent.vue";
import {useRouter} from "vue-router";
import SurveyNodeDialogContent from "@/flowcharts/Components/SurveyNodeDialogContent.vue";
import FlowchartProductRecommendations
    from "@/flowcharts/Components/flowchart-modal/FlowchartProductRecommendations.vue";
import FlowchartNodeSequences
    from "@/flowcharts/Components/flowchart-modal/FlowchartNodeSequences.vue";
import FlowchartNodeHook from "@/flowcharts/Components/flowchart-modal/FlowchartNodeHook.vue";
import {ProductRecommendation} from "@/core/interfaces/Product";
import {NotificationSequence} from "@/notification-sequences/interfaces/NotificationSequence";
import {FlowchartInstanceSession} from "@/flowcharts/composables/useFlowchartInstance";

const props = defineProps<ActionComponentProps & {
    /** The flowchart instance of the current passage. */
    instance: FlowchartInstanceSession
}>();
const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'resolved', action: ResolvedAction): void;
}>();
const router = useRouter();
const loading = ref<boolean>(false);

const addedRecommendations = ref<ProductRecommendation[]>([]);

/** The pharmacist comment printed on the documents that accept one. */
const comment = ref<string>('');

/** Whether a notification sequence detail page is currently open in the modal. */
const viewingSequence = ref<boolean>(false);

/**
 * The dynamic component to load.
 */
const component = computed(() => {
    switch (props.node?.action_type) {
        case 'print':
            return PrintActionDialogContent;
        case 'survey':
            return SurveyNodeDialogContent;
        case 'contact':
        default:
            return ContactActionDialogContent;
    }
});

/** Whether the current node is a survey node. */
const isSurveyNode = computed(() => props.node?.action_type === 'survey');

/** The advice seeded on the node, whatever its action. */
const hook = computed((): string | undefined => {
    return (props.node?.action_payload as { hook?: string } | null)?.hook;
});

/** The notification sequences linked to the node. */
const nodeSequences = computed((): NotificationSequence[] => props.node?.notification_sequences ?? []);

/** The available options for the selected nodes.*/
const selectedEdges = computed(() => props.flowchart.selectedEdges());

/** The selected options values for each edge.*/
const optionValues = ref<Record<string, Record<string, string | string[]>>>({});

/** The path with the selected options.*/
const pathWithOptions = computed((): EdgePath => selectedEdges.value.map(edge => {
    const edgeSurveyValues = optionValues.value[edge.id] || {};

    const options = Object.entries(edgeSurveyValues).reduce((current, [fieldSlug, value]) => {
        return {
            ...current,
            [fieldSlug]: value,
        };
    }, {});

    return {
        slug: edge.slug,
        options: options,
    };
}));

/** Whether the path has options to check.*/
const hasOptions = computed((): boolean => {
    return !!selectedEdges.value.filter(edge => {

        return edge.options;
    }).length;
});

/** Whether the save button should be disabled.*/
const actionDisabled = computed((): boolean => {
    if (!hasOptions.value) return false;

    for (const edge of selectedEdges.value) {
        const edgeHasOptions = !!edge.options;

        if (edgeHasOptions) {
            const edgeOptionValues = optionValues.value[edge.id] || {};
            const optionFields = Object.keys(edge.options!);

            for (const fieldSlug of optionFields) {
                const required = edge.options![fieldSlug].required || false;
                const value = edgeOptionValues[fieldSlug];
                if (required && (!value || (Array.isArray(value) && value.length === 0))) {
                    return true;
                }
            }
        }
    }

    return false;
});

/**
 * Get the field value for the edge and field.
 */
const fieldValue = (edgeId: number, fieldSlug: string, field: OptionField) => {
    return optionValues.value[edgeId]?.[fieldSlug] || (field.type === 'multiple_choice' ? [] : '');
};

/**
 * Handle option field change.
 */
const handleOptionChange = (edgeId: number, fieldSlug: string, value: string | string[]) => {
    if (!optionValues.value[edgeId]) {
        optionValues.value[edgeId] = {};
    }
    optionValues.value[edgeId][fieldSlug] = value;
};

/**
 * The path to save once the pharmacist acts on the terminal node: the products and
 * the comment go in the reserved options of the last item, to reprint from the history.
 */
const completedPath = (): FlowchartInstancePath => {
    const path = pathWithOptions.value;
    const last = path.at(-1);

    if (!last) {
        return path;
    }

    const terminalOptions = {
        ...(addedRecommendations.value.length && {
            [FLOWCHART_INSTANCE_OPTIONS.recommendations]: addedRecommendations.value.map(recommendation => recommendation.id),
        }),
        ...(comment.value && {[FLOWCHART_INSTANCE_OPTIONS.comment]: comment.value}),
    };

    return [...path.slice(0, -1), {...last, options: {...last.options, ...terminalOptions}}];
};

/**
 * Save the complete path in the instance of the passage.
 */
const saveInstance = () => props.instance.save(props.flowchart.model().id, props.consumer, completedPath());

// The node action chip opens the modal before the click highlights the path to the node.
watch([() => props.node, () => selectedEdges.value.map(edge => edge.id).join()], ([node]) => {
    if (node?.type === 'output') {
        props.instance.open(props.flowchart.model().id, props.consumer, pathWithOptions.value);
    }
}, {immediate: true});

/**
 * Closes the modal.
 * @param openState
 */
const close = (openState: boolean) => {
    if (!openState) {
        emits('close');
    }
};

/**
 * Save the path and close the TROD.
 */
const saveAndExit = async () => {
    loading.value = true;
    await saveInstance();
    emits('close');
    loading.value = false;
    router.go(-1);
};
</script>

<template>
  <Dialog :open="!!node" @update:open="close">
    <DialogContent
      class="[&>button]:hidden max-w-screen max-h-full bg-transparent flex justify-center gap-4"
      @interact-outside="$emit('close')"
    >
      <div class="bg-white rounded-lg p-6 flex-1 relative max-w-[700px] overflow-y-auto overflow-x-hidden">
        <DialogClose class="absolute right-6">
          <X :size="16" />
        </DialogClose>
        <FlowchartNodeSequences
          :sequences="nodeSequences"
          :consumer="consumer"
          @update:viewing-sequence="viewingSequence = $event"
        >
          <DialogHeader>
            <DialogTitle>{{ node?.label }}</DialogTitle>
            <DialogDescription class="mb-2">
              <div v-if="isSurveyNode" class="text-center">
                Répondez au questionnaire ci-dessous.
              </div>
              <div v-else-if="hasOptions" class="text-center">
                Précisez les choix ci-dessous.
              </div>
              <div v-else class="text-center">
                Tout est clair.
              </div>
            </DialogDescription>
          </DialogHeader>
          <div v-if="node.file?.url" class="mb-4">
            <img :src="node.file.url" class="w-full object-contain" alt="node image">
          </div>
          <FlowchartNodeHook v-if="hook" :content="hook" class="mb-4" />
          <component
            :is="component"
            :flowchart="flowchart"
            :node="node"
            :consumer="consumer"
            :survey-verdicts="surveyVerdicts"
            :path="pathWithOptions"
            :disabled="actionDisabled"
            :field-value="fieldValue"
            :selected-edges="selectedEdges"
            :action-disabled="actionDisabled"
            :loading="loading"
            :recommendations="addedRecommendations"
            v-model:comment="comment"
            @handle-option-change="handleOptionChange"
            @print="saveInstance"
            @save-and-exit="saveAndExit"
            @resolved="$emit('resolved', $event)"
          />
        </FlowchartNodeSequences>
      </div>
      <div
        v-if="!isSurveyNode && flowchart.model().recommendations?.length"
        class="relative max-h-full"
      >
        <FlowchartProductRecommendations
          v-model="addedRecommendations"
          class="overflow-y-auto max-h-full pr-2"
          :recommendations="flowchart.recommendations()"
        />
        <div
          class="absolute inset-0 rounded-lg bg-[#14122a] transition-opacity duration-300"
          :class="viewingSequence ? 'opacity-60 pointer-events-auto' : 'opacity-0 pointer-events-none'"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
